import React from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MicIcon from "@mui/icons-material/Mic";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import NavigationIcon from "@mui/icons-material/Navigation";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { VOICE_VIDEO_END_BEHAVIOR_OPTIONS } from "../../services/aiVoiceAssistantApi";
import { normalizeStringList } from "./systemConfigurationUtils";

const VoiceCommandsConfigPanel = ({
  t,
  aiVoiceCommands,
  updateVoiceConfig,
  subscribers,
  selectedVoiceSubscriberId,
  setSelectedVoiceSubscriberId,
  selectedVoiceSubscriberKey,
  selectedVoiceSubscriber,
  selectedVoiceActions,
  addVoiceBasicAction,
  deleteVoiceBasicAction,
  updateVoiceBasicAction,
  updateVoiceBasicMedia,
  copyDefaultVoiceActionsToSubscriber,
  clearSubscriberVoiceActions,
  voiceMediaLibrary,
  uploadingVoiceActionIndex,
  handleVoiceMediaUpload,
  updateVoicePage,
  updateVoiceEntity,
}) => (
  <>
    <section className="system-config-panel">
      <div className="system-config-panel-head">
        <div>
          <h3>{t.voiceTitle || "Web Speech voice commands"}</h3>
          <p>{t.voiceDescription || "Choose between basic wake-word media popups and advanced server-validated navigation commands."}</p>
        </div>
      </div>

      <div className="system-config-voice-hero">
        <label className="system-config-switch-card">
          <span>
            <strong>{t.voiceEnableTitle || "Enable voice commands"}</strong>
            <small>{t.voiceEnableDescription || "Users with AI voice command permission can turn browser listening on or off from their profile menu."}</small>
          </span>
          <input
            type="checkbox"
            checked={aiVoiceCommands.enabled !== false}
            onChange={(event) => updateVoiceConfig({ enabled: event.target.checked })}
          />
        </label>

        <div className="system-config-options compact" role="radiogroup" aria-label="Voice command mode">
          {[
            {
              key: "basic",
              icon: <FolderOpenIcon />,
              title: t.voiceModeBasicTitle || "Basic Web Speech API",
              description: t.voiceModeBasicDescription || "Wake word or sentence opens a selected image or video popup.",
            },
            {
              key: "advanced",
              icon: <NavigationIcon />,
              title: t.voiceModeAdvancedTitle || "Advanced commands",
              description: t.voiceModeAdvancedDescription || "Wake word plus command can navigate pages and open permitted entity details.",
            },
          ].map((option) => {
            const selected = aiVoiceCommands.mode === option.key;
            return (
              <button
                type="button"
                key={option.key}
                className={`system-config-option ${selected ? "selected" : ""}`}
                onClick={() => updateVoiceConfig({ mode: option.key })}
                role="radio"
                aria-checked={selected}
              >
                <span className="system-config-option-icon">{option.icon}</span>
                <span>
                  <strong>{option.title}</strong>
                  <small>{option.description}</small>
                </span>
                <span className="system-config-radio" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="system-config-live-voice-card">
        <div className="system-config-panel-head compact">
          <div>
            <h4>{t.voiceLiveTitle || "Hey Sama live conversation"}</h4>
            <p>{t.voiceLiveDescription || "After the user says the wake phrase, keep one secure WebSocket session open and stream PCM16 microphone audio continuously. The AI handles VAD and always replies with streamed voice."}</p>
          </div>
          <label className="system-config-inline-switch">
            <span>{t.voiceLiveEnabled || "Enable live conversation"}</span>
            <input
              type="checkbox"
              checked={aiVoiceCommands.liveConversation?.enabled !== false}
              onChange={(event) =>
                updateVoiceConfig({
                  liveConversation: {
                    ...(aiVoiceCommands.liveConversation || {}),
                    enabled: event.target.checked,
                  },
                })
              }
            />
          </label>
        </div>

        <div className="system-config-editor-grid voice">
          <label>
            <span>{t.voiceLiveVoice || "Live response voice"}</span>
            <input
              value={aiVoiceCommands.liveConversation?.voice || "nova"}
              onChange={(event) =>
                updateVoiceConfig({
                  liveConversation: {
                    ...(aiVoiceCommands.liveConversation || {}),
                    voice: event.target.value,
                  },
                })
              }
              placeholder="nova"
              maxLength={32}
              autoComplete="off"
              spellCheck="false"
            />
          </label>
          <label>
            <span>{t.voiceLiveEndOfTurnSilence || "Silence before sending (seconds)"}</span>
            <input
              type="number"
              min="0"
              max="10"
              step="0.1"
              inputMode="decimal"
              value={Number(aiVoiceCommands.liveConversation?.endOfTurnSilenceMs ?? 2000) / 1000}
              onChange={(event) => {
                const seconds = Number(event.target.value);
                if (!Number.isFinite(seconds)) return;
                updateVoiceConfig({
                  liveConversation: {
                    ...(aiVoiceCommands.liveConversation || {}),
                    endOfTurnSilenceMs: Math.round(Math.min(10, Math.max(0, seconds)) * 1000),
                  },
                });
              }}
              aria-describedby="voice-live-silence-hint"
            />
            <small id="voice-live-silence-hint">
              {t.voiceLiveEndOfTurnSilenceHint || "How long Sama waits after the user becomes quiet before ending the turn. 0 uses the AI server's normal VAD timing; 1.5–2.0 seconds is recommended."}
            </small>
          </label>
          <label className="system-config-wide">
            <span>{t.voiceLiveStopPhrases || "Turn-off phrases"}</span>
            <input
              value={(aiVoiceCommands.liveConversation?.stopPhrases || []).join(", ")}
              onChange={(event) =>
                updateVoiceConfig({
                  liveConversation: {
                    ...(aiVoiceCommands.liveConversation || {}),
                    stopPhrases: normalizeStringList(event.target.value.split(/[,،;\n]+/)),
                  },
                })
              }
              placeholder="stop sama, end sama, توقف سما"
              maxLength={2400}
              autoComplete="off"
            />
            <small>{t.voiceLiveStopHint || "A final transcript must exactly match one of these phrases. Matching turns off Hey Sama completely and closes the live session."}</small>
          </label>
        </div>
      </div>

      <div className="system-config-editor-grid voice">
        <label>
          <span>{t.voiceWakeWords || "Wake words"}</span>
          <input
            value={(aiVoiceCommands.wakeWords || []).join(", ")}
            onChange={(event) => updateVoiceConfig({ wakeWords: normalizeStringList(event.target.value.split(",")) })}
            placeholder="sama, سما"
          />
        </label>
        <label>
          <span>{t.voiceLanguages || "Speech languages"}</span>
          <input
            value={(aiVoiceCommands.languages || []).join(", ")}
            onChange={(event) => updateVoiceConfig({ languages: normalizeStringList(event.target.value.split(",")) })}
            placeholder="en-US, ar-SA"
          />
        </label>
        <label className="system-config-wide">
          <span>{t.voiceDeniedMessage || "No permission response"}</span>
          <input
            value={aiVoiceCommands.deniedMessage || ""}
            onChange={(event) => updateVoiceConfig({ deniedMessage: event.target.value })}
          />
        </label>
        <label className="system-config-wide">
          <span>{t.voiceUnknownMessage || "Unknown request response"}</span>
          <input
            value={aiVoiceCommands.unknownMessage || ""}
            onChange={(event) => updateVoiceConfig({ unknownMessage: event.target.value })}
          />
        </label>
      </div>
    </section>

    <section className="system-config-panel">
      <div className="system-config-panel-head">
        <div>
          <h3>{t.voiceBasicActionsTitle || "Basic media actions"}</h3>
          <p>
            {selectedVoiceSubscriberKey
              ? (t.voiceSubscriberActionsDescription || "Configure the media shown only for the selected subscriber.")
              : (t.voiceBasicActionsDescription || "Configure the default spoken phrases and media used when a subscriber has no override.")}
          </p>
        </div>
        <button type="button" className="system-config-add-btn" onClick={addVoiceBasicAction}>
          <AddIcon /> {t.voiceAddAction || "Add action"}
        </button>
      </div>

      <div className="system-config-voice-subscriber-bar">
        <label>
          <span>{t.voiceSubscriberScope || "Subscriber media scope"}</span>
          <select
            value={selectedVoiceSubscriberId}
            onChange={(event) => setSelectedVoiceSubscriberId(event.target.value)}
          >
            <option value="default">{t.voiceDefaultScope || "Global default media"}</option>
            {subscribers.map((subscriber) => (
              <option key={subscriber.id} value={subscriber.id}>
                {subscriber.name || subscriber.email || `Subscriber ${subscriber.id}`}
              </option>
            ))}
          </select>
        </label>
        <div>
          <strong>
            {selectedVoiceSubscriberKey
              ? (selectedVoiceSubscriber?.name || `${t.voiceSubscriber || "Subscriber"} ${selectedVoiceSubscriberKey}`)
              : (t.voiceDefaultScope || "Global default media")}
          </strong>
          <small>
            {selectedVoiceSubscriberKey
              ? (selectedVoiceActions.length
                  ? (t.voiceOverrideActive || "This subscriber has its own media commands.")
                  : (t.voiceOverrideEmpty || "No override yet. Add actions or copy the global default."))
              : (t.voiceDefaultScopeHint || "These actions are used for subscribers without a custom media setup.")}
          </small>
        </div>
        {selectedVoiceSubscriberKey ? (
          <div className="system-config-voice-subscriber-actions">
            <button type="button" className="system-config-add-btn secondary" onClick={copyDefaultVoiceActionsToSubscriber}>
              {t.voiceCopyDefault || "Copy default"}
            </button>
            <button type="button" className="system-config-add-btn secondary" onClick={clearSubscriberVoiceActions}>
              {t.voiceClearOverride || "Clear override"}
            </button>
          </div>
        ) : null}
      </div>

      <div className="system-config-voice-actions">
        {selectedVoiceActions.map((action, index) => (
          <div className="system-config-voice-row" key={action.id || index}>
            <div className="system-config-voice-row-title">
              <MicIcon />
              <strong>{action.label || t.voiceMediaAction || "Voice media action"}</strong>
              <button type="button" className="system-config-icon-btn" onClick={() => deleteVoiceBasicAction(index)} aria-label={t.voiceDeleteAction || "Delete voice action"}>
                <DeleteOutlineIcon />
              </button>
            </div>
            <div className="system-config-editor-grid voice">
              <label>
                <span>{t.voiceActionLabel || "Action label"}</span>
                <input value={action.label || ""} onChange={(event) => updateVoiceBasicAction(index, { label: event.target.value })} />
              </label>
              <label>
                <span>{t.voicePhrases || "Words / sentences"}</span>
                <input
                  value={(action.phrases || []).join(", ")}
                  onChange={(event) => updateVoiceBasicAction(index, { phrases: normalizeStringList(event.target.value.split(",")) })}
                  placeholder="sama, sama show intro"
                />
              </label>
              <label className="system-config-wide">
                <span>{t.voiceServerMedia || "Server media"}</span>
                <div className="system-config-media-picker">
                  <select
                    value={action.action?.media?.path || ""}
                    onChange={(event) => {
                      const selected = voiceMediaLibrary.find((item) => item.path === event.target.value);
                      updateVoiceBasicMedia(index, {
                        path: event.target.value,
                        title: selected?.title || action.action?.media?.title || "",
                        mediaType: selected?.mediaType || action.action?.media?.mediaType || "file",
                      });
                    }}
                  >
                    <option value="">{t.voiceChooseServerFile || "Choose a server file"}</option>
                    {voiceMediaLibrary.map((item) => (
                      <option key={item.path} value={item.path}>
                        {String(item.mediaType || "file").toUpperCase()} / {item.path}
                      </option>
                    ))}
                  </select>
                  <label className={`system-config-upload-btn ${uploadingVoiceActionIndex === index ? "uploading" : ""}`}>
                    <CloudUploadIcon />
                    <span>{uploadingVoiceActionIndex === index ? (t.voiceUploading || "Uploading...") : (t.voiceUploadAndAssign || "Upload & assign")}</span>
                    <input
                      type="file"
                      accept="image/*,video/mp4,video/webm,video/ogg,video/quicktime"
                      disabled={uploadingVoiceActionIndex !== null}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        handleVoiceMediaUpload(index, file);
                      }}
                    />
                  </label>
                </div>
              </label>
              <label>
                <span>{t.voicePopupTitle || "Popup title"}</span>
                <input
                  value={action.action?.media?.title || ""}
                  onChange={(event) => updateVoiceBasicMedia(index, { title: event.target.value })}
                />
              </label>
              <label>
                <span>{t.voiceMediaType || "Media type"}</span>
                <select
                  value={action.action?.media?.mediaType || "image"}
                  onChange={(event) => updateVoiceBasicMedia(index, { mediaType: event.target.value })}
                >
                  <option value="image">{t.voiceMediaImage || "Image"}</option>
                  <option value="video">{t.voiceMediaVideo || "Video"}</option>
                  <option value="file">{t.voiceMediaFile || "File"}</option>
                </select>
              </label>
              <label>
                <span>{t.voiceVideoEndBehavior || "Video end behavior"}</span>
                <select
                  value={action.action?.media?.videoEndBehavior || "close"}
                  onChange={(event) => updateVoiceBasicMedia(index, { videoEndBehavior: event.target.value })}
                >
                  {VOICE_VIDEO_END_BEHAVIOR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        ))}
        {selectedVoiceActions.length === 0 ? <div className="system-config-type-empty">{t.voiceNoActions || "No media actions configured."}</div> : null}
      </div>
    </section>

    <section className="system-config-panel">
      <div className="system-config-panel-head">
        <div>
          <h3>{t.voiceAdvancedTitle || "Advanced navigation dictionary"}</h3>
          <p>{t.voiceAdvancedDescription || "Tune the command words used after the wake word for page navigation and entity details."}</p>
        </div>
      </div>

      <div className="system-config-voice-mini-grid">
        <div>
          <h4>{t.voicePageNavigation || "Page navigation"}</h4>
          {(aiVoiceCommands.advanced?.pages || []).map((page, index) => (
            <div className="system-config-voice-mini-row" key={page.id || index}>
              <strong>{page.label}</strong>
              <small>{page.path}</small>
              <input
                value={(page.phrases || []).join(", ")}
                onChange={(event) => updateVoicePage(index, { phrases: normalizeStringList(event.target.value.split(",")) })}
                placeholder="open dashboard, go dashboards"
              />
            </div>
          ))}
        </div>
        <div>
          <h4>{t.voiceEntityDetails || "Entity details"}</h4>
          {(aiVoiceCommands.advanced?.entities || []).map((entity, index) => (
            <div className="system-config-voice-mini-row" key={entity.id || index}>
              <strong>{entity.label}</strong>
              <small>{entity.pathPrefix}/:id</small>
              <input
                value={(entity.phrases || []).join(", ")}
                onChange={(event) => updateVoiceEntity(index, { phrases: normalizeStringList(event.target.value.split(",")) })}
                placeholder="open device details, open device"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default VoiceCommandsConfigPanel;
